import NiceModal from '@ebay/nice-modal-react';

import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { updateDynamicListsInPage } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAppTracking from 'hooks/useAppTracking';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';

import MobileVerifyModal from 'modals/MobileVerifyModal';

import ChargeAccountModal from 'pages/Topbar/ChargeAccount/ChargeAccountModal';

import fetchRemainingAmount from 'services/common/fetchRemainingAmount';
import { IMPORT, ORDER_ACTIONS } from 'services/consts';

import { setIsCreateShipmentLoading } from 'stores/layoutSlice';

import AppLogger from 'utils/AppLogger';
import { store } from 'utils/configureStore';
import PATHS, { mergePaths } from 'utils/paths';

const useCreateShipment = ({ type } = {}) => {
	const dispatch = useDispatch();
	const { Country } = useLocale();
	const axiosPrivate = useAxiosPrivate();
	const navigate = useNavigate();
	const { showError } = useAlert();
	const { updateOnboarding } = useAppTracking();
	const user = useSelector((state) => state.user);
	const credit = user.remainingAmount;

	const isTrUser = useMemo(() => Country === 'TR', [Country]);

	const handleCreateShipment = async (payload, onShipmentSuccess, onShipmentError, onIncomplete) => {
		const mobileVerified = store.getState().user.companyAdminVerified;
		if (isTrUser && !user?.nationalIDVerified && user.planName !== 'EnterprisePackage') {
			showError({
				message: 'youCanVerifyYourID',
				link: 'goToProfile',
				linkAction: () => {
					navigate(PATHS.USER_SETTINGS);
				}
			});
			onIncomplete && onIncomplete();
			return;
		}
		if (!mobileVerified) {
			NiceModal.show(MobileVerifyModal);
			onIncomplete && onIncomplete();
			return;
		}
		const url = type === 'import' ? IMPORT.CREATE_IMPORT_SHIPMENT : ORDER_ACTIONS.CREATE_SHIPMENT;
		await axiosPrivate
			.post(url, payload)
			.then((response) => {
				if (response?.status === 200) {
					const shipment = response?.data?.data?.shipments[0];

					if (shipment.error) {
						showError({
							header: 'error',
							message: shipment?.aiErrorMessage || 'error.O004',
							link: 'shipmentErrorLogs',
							linkAction: () => {
								window.open(mergePaths(PATHS.SYSTEM_LOGS, PATHS.SHIPMENT_ERROR_LOGS));
							}
						});
						AppLogger.error(response);
						onShipmentError && onShipmentError(shipment);
					} else {
						onShipmentSuccess && onShipmentSuccess(shipment);

						const { totalDue } = payload;

						if (totalDue > 0) {
							updateOnboarding(['codShipmentCheck']);
						}

						dispatch(updateDynamicListsInPage());

						if (type === 'import') {
							window.dataLayer.push({
								/* eslint-disable camelcase */
								event: 'import_from_china',
								first_name: user?.firstName || '',
								last_name: user?.lastName || '',
								phone: user?.phone || '',
								email: user?.email || '',
								plan: user?.planName || '',
								country: user?.localization?.label || '',
								userID: user?.ID || ''
								/* eslint-enable camelcase */
							});
						}
					}
				} else {
					AppLogger.error(response);
					showError();
					onIncomplete && onIncomplete();
				}
			})
			.catch((error) => {
				if (error.response?.data?.code === 'error.O004') {
					showError({
						header: 'error',
						message: 'error.O004',
						link: 'shipmentErrorLogs',
						linkAction: () => {
							window.open(mergePaths(PATHS.SYSTEM_LOGS, PATHS.SHIPMENT_ERROR_LOGS));
						}
					});
				} else if (error.response?.data?.code === 'error.O005') {
					showError({
						header: 'error',
						message: 'error.O005'
					});
				} else {
					AppLogger.error(error);
					showError({
						message: error?.response?.data?.code || error?.response?.data?.message
					});
				}
				onIncomplete && onIncomplete();
			});

		await fetchRemainingAmount();
	};

	const createShipment = async (payload, onShipmentSuccess, onShipmentError, onIncomplete) => {
		dispatch(setIsCreateShipmentLoading(true));

		const { items } = payload;
		const price = items.reduce((acc, item) => acc + item.price, 0);

		if (credit < price) {
			if (!store.getState().user.companyAdminVerified) {
				const result = await NiceModal.show(MobileVerifyModal);
				if (!result && !store.getState().user.companyAdminVerified) {
					dispatch(setIsCreateShipmentLoading(false));
					onIncomplete?.();
					return;
				}
			}
			await NiceModal.show(ChargeAccountModal, {
				defaultSelectedAmount: Math.ceil(price - credit),
				isShowSuccessTab: false
			}).then(async () => {
				const { remainingAmount } = await fetchRemainingAmount();
				if (remainingAmount >= price) {
					await handleCreateShipment(payload, onShipmentSuccess, onShipmentError, onIncomplete);
				} else {
					showError({
						header: 'priceCalculator.notEnoughCreditHeader',
						message: 'priceCalculator.notEnoughCreditMessage'
					});
					onIncomplete && onIncomplete();
				}
				dispatch(setIsCreateShipmentLoading(false));
			});
		} else {
			await handleCreateShipment(payload, onShipmentSuccess, onShipmentError, onIncomplete);
			dispatch(setIsCreateShipmentLoading(false));
		}
	};

	return createShipment;
};

export default useCreateShipment;
